import React, { useState } from 'react';
import { FlaskConical, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useDemoMode } from '../contexts/DemoModeContext';
import { useAuth } from '../App';
import { toast } from 'sonner';

/**
 * DemoModeToggleCard — Enter/Exit Demo Mode card.
 *
 * variant:
 *   "card"    → full card for Dashboard page (primary CTA)
 *   "inline"  → compact inline block for Settings / Channels pages
 */
const DemoModeToggleCard = ({ variant = 'card' }) => {
  const { user } = useAuth();
  const { active, loading, enter, exit } = useDemoMode();
  const [busy, setBusy] = useState(false);

  // Only owner/admin/super_admin can toggle
  const canToggle =
    user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin;

  if (!canToggle) return null;

  const handleEnter = async () => {
    setBusy(true);
    try {
      await enter();
      toast.success('Demo Mode activated. A mock channel and preview calendar have been seeded.');
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to enter Demo Mode.');
      setBusy(false);
    }
  };

  const handleExit = async () => {
    setBusy(true);
    try {
      await exit();
      toast.success('Demo Mode deactivated. Demo data removed.');
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to exit Demo Mode.');
      setBusy(false);
    }
  };

  if (variant === 'inline') {
    return (
      <div
        className="flex items-center justify-between gap-4 p-4 border rounded-lg bg-muted/30"
        data-testid="demo-mode-toggle-inline"
      >
        <div className="flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-sm">
              {active ? 'Demo Mode is active' : 'Explore with Demo Mode'}
            </p>
            <p className="text-xs text-muted-foreground">
              {active
                ? 'Using mock channel + preview calendar. Exit to connect real providers.'
                : 'Try the product with a mock channel, preview calendar, and seeded bookings. No real data affected.'}
            </p>
          </div>
        </div>
        {active ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExit}
            disabled={busy || loading}
            data-testid="demo-mode-exit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Exit Demo Mode
          </Button>
        ) : (
          <Button
            size="sm"
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            onClick={handleEnter}
            disabled={busy || loading}
            data-testid="demo-mode-enter-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Enter Demo Mode
          </Button>
        )}
      </div>
    );
  }

  // card variant
  return (
    <Card
      className={`border-2 ${
        active
          ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700'
          : 'border-dashed border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-700'
      }`}
      data-testid="demo-mode-toggle-card"
    >
      <CardContent className="pt-6 pb-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                active ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-emerald-100 dark:bg-emerald-900/40'
              }`}
            >
              <FlaskConical
                className={`w-5 h-5 ${active ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
              />
            </div>
            <div>
              <p className="font-semibold text-base">
                {active ? 'Demo Mode is active' : 'Try Flowteras in Demo Mode'}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
                {active
                  ? 'You are using a mock channel and a preview calendar with seeded demo bookings. Exit to return to real setup.'
                  : 'Seed a mock channel, preview calendar, and sample bookings. No WhatsApp or Google connection required. Your real data is untouched.'}
              </p>
            </div>
          </div>
          {active ? (
            <Button
              variant="outline"
              onClick={handleExit}
              disabled={busy || loading}
              data-testid="demo-mode-exit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Exit Demo Mode
            </Button>
          ) : (
            <Button
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={handleEnter}
              disabled={busy || loading}
              data-testid="demo-mode-enter-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Enter Demo Mode
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DemoModeToggleCard;
